import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  Clock, 
  AlertTriangle, 
  TrendingUp,
  Calendar,
  ArrowRight,
  Database,
  Loader2
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { formatDate } from '@/lib/utils';

const NEEDLE_LABELS: Record<number, string> = {
  1: 'Nadel 1 (Grundunterlagen)',
  2: 'Nadel 2 (Vollstreckung)',
  3: 'Nadel 3 (Sonstiges)',
};

const TYPE_LABELS: Record<string, string> = {
  DOCUMENT: 'Dokument',
  NOTE: 'Vermerk',
  EVENT: 'Ereignis',
  CORRESPONDENCE: 'Schriftverkehr',
  DECISION: 'Entscheidung',
};

export function DashboardPage() {
  const { user } = useAuth();

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.getDashboardStats(),
    refetchInterval: 60000, // Refresh every minute
  });

  const statCards = [
    {
      title: 'Aktive Gefangene',
      value: stats?.activePrisoners ?? '-',
      subtitle: stats ? `von ${stats.totalPrisoners} gesamt` : undefined,
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Offene Dispositionen',
      value: stats?.pendingDispositions ?? '-',
      subtitle: 'warten auf Bearbeitung',
      icon: Clock,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
    },
    {
      title: 'Dokumente heute',
      value: stats?.documentsToday ?? '-',
      subtitle: stats ? `${stats.totalDocuments} insgesamt` : undefined,
      icon: FileText,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'OCR ausstehend',
      value: stats?.warnings ?? '-',
      subtitle: 'Dokumente warten auf OCR',
      icon: AlertTriangle,
      color: stats?.warnings && stats.warnings > 0 ? 'text-red-500' : 'text-gray-400',
      bgColor: stats?.warnings && stats.warnings > 0 ? 'bg-red-500/10' : 'bg-gray-500/10',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">
            Willkommen zurück, {user?.displayName || user?.username}
          </p>
        </div>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Fehler beim Laden der Dashboard-Daten.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">
            Willkommen zurück, {user?.displayName || user?.username}
          </p>
        </div>
        <Link to="/prisoners/new">
          <Button>
            <Users className="mr-2 h-4 w-4" />
            Neue Aufnahme
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <div className={`rounded-lg p-2 ${stat.bgColor}`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              {stat.subtitle && (
                <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Records */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Zuletzt bearbeitet
              </CardTitle>
              <CardDescription>
                Kürzlich aktualisierte Akteneinträge
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.recentRecords && stats.recentRecords.length > 0 ? (
              <div className="space-y-4">
                {stats.recentRecords.map((record) => (
                  <Link
                    key={record.id}
                    to={`/prisoners/${record.prisoner.id}`}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{record.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {record.prisoner.lastName}, {record.prisoner.firstName} ({record.prisoner.prisonerNumber})
                      </p>
                    </div>
                    <div className="ml-4 text-right">
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                        {TYPE_LABELS[record.type] || record.type}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(record.updatedAt)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Keine kürzlich bearbeiteten Einträge.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Admissions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Letzte Zugänge
              </CardTitle>
              <CardDescription>
                Kürzlich aufgenommene Gefangene
              </CardDescription>
            </div>
            <Link to="/prisoners">
              <Button variant="ghost" size="sm">
                Alle anzeigen
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recentAdmissions && stats.recentAdmissions.length > 0 ? (
              <div className="space-y-3">
                {stats.recentAdmissions.map((prisoner) => (
                  <Link
                    key={prisoner.id}
                    to={`/prisoners/${prisoner.id}`}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent transition-colors"
                  >
                    <div>
                      <p className="font-medium">
                        {prisoner.lastName}, {prisoner.firstName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {prisoner.prisonerNumber}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {formatDate(prisoner.admissionDate)}
                      </p>
                      <p className="text-xs text-muted-foreground">Aufnahme</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Keine aktuellen Zugänge.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Releases */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Anstehende Entlassungen
            </CardTitle>
            <CardDescription>
              Entlassungen in den nächsten 2 Monaten ({stats?.upcomingReleases?.length || 0} Gefangene)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.upcomingReleases && stats.upcomingReleases.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                {stats.upcomingReleases.map((prisoner) => (
                  <Link
                    key={prisoner.id}
                    to={`/prisoners/${prisoner.id}`}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent transition-colors"
                  >
                    <div>
                      <p className="font-medium">
                        {prisoner.lastName}, {prisoner.firstName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {prisoner.prisonerNumber}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-orange-600">
                        {formatDate(prisoner.releaseDate!)}
                      </p>
                      <p className="text-xs text-muted-foreground">Entlassung</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Keine anstehenden Entlassungen in den nächsten 2 Monaten.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Documents by Needle */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Dokumente nach Nadel
            </CardTitle>
            <CardDescription>
              Verteilung der Akteneinträge
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.documentsByNeedle && stats.documentsByNeedle.length > 0 ? (
              <div className="space-y-4">
                {stats.documentsByNeedle.map((item) => {
                  const total = stats.documentsByNeedle.reduce((sum, i) => sum + i.count, 0);
                  const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  
                  return (
                    <div key={item.needle} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>{NEEDLE_LABELS[item.needle] || `Nadel ${item.needle}`}</span>
                        <span className="font-medium">{item.count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            item.needle === 1
                              ? 'bg-blue-500'
                              : item.needle === 2
                              ? 'bg-green-500'
                              : 'bg-orange-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between text-sm font-medium">
                    <span>Gesamt</span>
                    <span>{stats.documentsByNeedle.reduce((sum, i) => sum + i.count, 0)} Einträge</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Keine Daten verfügbar.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
